import React, {useCallback, useEffect, useState} from "react";
import ErrorBoundary from "../../../../components/shared/error/ErrorBoundary";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import {Header} from "../../../../components/dashboards/components/shared/Header";
import {AlertCircle} from "lucide-react";
import {getBrazilianStatus} from "../../../../utils/detectBrazil";
import {volleyballDashPtBR} from "../../../../components/mappings/sports-dash-mappings/volleyball-dash.pt-br";
import VolleyballService from "../services/volleyballService";
import {USA} from "../constants/countries";
import {TABS_ID} from "../components/DesktopNavigation";
import MobileTabNavigation from "../components/mobile/MobileTabNavigation";
import MobileGamesView from "../components/mobile/MobileGamesView";
import MobileStandingsView from "../components/mobile/MobileStandingsView";
import MobileTeamsView from "../components/mobile/MobileTeamsView";
import {VOLLEYBALL_HEADER_COPY} from "../constants/headerContent";
import RailNavigation from '../../../../components/dashboards/mobile/RailNavigation';
import { CountrySheet, LeagueSheet } from '../../../../components/dashboards/mobile/FilterSheets';
import useVolleyballRailFilters from '../../../../components/dashboards/mobile/hooks/useVolleyballRailFilters';
import { DatePickerSheet } from '../../../../components/dashboards/components/shared/date-picker';
import { ensureISODate } from '../../../../components/dashboards/utils/dateUtils';
import MobileSportsBar from '../../../../components/homePagePanel/MobileSportsBar';

const VolleyballMobileDashboard = () => {
  const [activeTab, setActiveTab] = useState(TABS_ID.GAMES);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [leagues, setLeagues] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [games, setGames] = useState([]);
  const [standings, setStandings] = useState([]);
  const [teams, setTeams] = useState([]);

  const [filters, setFilters] = useState({
    date: VolleyballService.getTodayDate(),
    country: '',
    league: '',
    season: ''
  });

  const [gamesPagination, setGamesPagination] = useState({
    currentPage: 1,
    perPage: 6,
    totalPages: 1
  });

  const [teamsPagination, setTeamsPagination] = useState({
    currentPage: 1,
    perPage: 6,
    totalPages: 1
  });

  const isBrazilian = getBrazilianStatus();
  const t = (text) => isBrazilian ? (volleyballDashPtBR[text] || text) : text;

  // Handle rail filter changes
  const handleRailFiltersChange = useCallback((nextFilters) => {
    setFilters(prev => ({
      ...prev,
      date: ensureISODate(nextFilters.date, prev.date),
      country: nextFilters.country || '',
      league: nextFilters.league || ''
    }));
  }, []);

  // Initialize rail filters hook
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    primaryTabs: railPrimaryTabs,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet,
    setShowDateSheet,
    showCountrySheet,
    setShowCountrySheet,
    showLeagueSheet,
    setShowLeagueSheet
  } = useVolleyballRailFilters({
    initialDate: filters.date,
    onFiltersChange: handleRailFiltersChange
  });

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));

    if (activeTab === TABS_ID.GAMES) {
      setGamesPagination(prev => ({ ...prev, currentPage: 1 }));
    } else if (activeTab === TABS_ID.TEAMS) {
      setTeamsPagination(prev => ({ ...prev, currentPage: 1 }));
    }
  }, [activeTab]);

  const handleGamesPageChange = useCallback((page) => {
    setGamesPagination(prev => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  const handleTeamsPageChange = useCallback((page) => {
    setTeamsPagination(prev => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [leaguesRes, seasonsRes] = await Promise.all([
          VolleyballService.getLeagues(),
          VolleyballService.getSeasons()
        ]);
        setLeagues(leaguesRes.response || []);
        setSeasons(seasonsRes.response || []);
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    };

    loadInitialData();
  }, []);

  const loadGames = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await VolleyballService.getGames(
        null,
        filters.date,
        filters.league || null,
        null,
        null,
        'UTC'
      );

      let filteredGames = response.response || [];

      if (filters.country) {
        const normalizedFilter = filters.country.toLowerCase();
        filteredGames = filteredGames.filter((game) => {
          const gameCode = game.country?.code?.toLowerCase();
          const gameName = game.country?.name?.toLowerCase();
          return gameCode === normalizedFilter || gameName === normalizedFilter;
        });
      }

      setGames(filteredGames);
      setGamesPagination(prev => {
        const totalPages = Math.max(1, Math.ceil(filteredGames.length / prev.perPage) || 1);
        const currentPage = Math.min(prev.currentPage, totalPages);
        return { ...prev, totalPages, currentPage };
      });
    } catch (err) {
      setError('Failed to load games');
      console.error('Load games error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.date, filters.league, filters.country]);

  const loadStandings = useCallback(async () => {
    if (!filters.league || !filters.season) {
      setStandings([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await VolleyballService.getStandings(filters.league, filters.season);
      const standingsData = response.response || [];
      setStandings(Array.isArray(standingsData[0]) ? standingsData[0] : standingsData);
    } catch (err) {
      setError('Failed to load standings');
      console.error('Load standings error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.league, filters.season]);

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (filters.country) params.country = filters.country;
      if (filters.league) params.league = filters.league;
      if (filters.season) params.season = filters.season;

      if (Object.keys(params).length === 0) {
        params.country = USA.name;
      }

      const response = await VolleyballService.getTeams(
        null, null, null, params.country, params.league, params.season, null
      );

      const teamsData = response.response || [];
      setTeams(teamsData);
      setTeamsPagination(prev => {
        const totalPages = Math.max(1, Math.ceil(teamsData.length / prev.perPage) || 1);
        const currentPage = Math.min(prev.currentPage, totalPages);
        return { ...prev, totalPages, currentPage };
      });
    } catch (err) {
      setError('Failed to load teams');
      console.error('Load teams error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.country, filters.league, filters.season]);

  useEffect(() => {
    if (activeTab === TABS_ID.GAMES) {
      loadGames();
    } else if (activeTab === TABS_ID.STANDINGS) {
      loadStandings();
    } else if (activeTab === TABS_ID.TEAMS) {
      loadTeams();
    }
  }, [activeTab, loadGames, loadStandings, loadTeams]);

  const refreshData = useCallback(() => {
    if (activeTab === TABS_ID.GAMES) {
      loadGames();
    } else if (activeTab === TABS_ID.STANDINGS) {
      loadStandings();
    } else if (activeTab === TABS_ID.TEAMS) {
      loadTeams();
    }
  }, [activeTab, loadGames, loadStandings, loadTeams]);

  return (
    <ErrorBoundary onReset={refreshData}>
      <div className="min-h-screen bg-[#050311] text-white">
        <UnifiedNavHeader
          title="Volleyball Dashboard"
          showBackButton={true}
        />

        {/* Mobile Sports Navigation Bar */}
        <MobileSportsBar />

        <div className="px-4 py-6 pb-24">
          <Header t={t} {...VOLLEYBALL_HEADER_COPY} />

          <div className="pt-4">
            <RailNavigation
              sport="volleyball"
              primaryTabs={railPrimaryTabs}
              filters={railFilterObjects}
              activeTab={railActiveTab}
              onTabChange={handleRailTabChange}
              onFilterChange={handleFilterClick}
              onReset={resetRailFilters}
              hasActiveFilters={railHasActiveFilters}
              className="border-y border-white/10 bg-[#050311]/95 backdrop-blur"
            />
          </div>

          <div className="mt-4 mb-4">
            <MobileTabNavigation
              activeTab={activeTab}
              onChange={setActiveTab}
              t={t}
            />
          </div>

          {error && (
            <div className="flex items-center gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              <AlertCircle className="text-red-300" size={18} />
              <p>{error}</p>
            </div>
          )}

          {activeTab === TABS_ID.GAMES && (
            <MobileGamesView
              games={games}
              filters={filters}
              onFilterChange={handleFilterChange}
              leagues={leagues}
              loading={loading}
              pagination={gamesPagination}
              onPageChange={handleGamesPageChange}
              t={t}
            />
          )}

          {activeTab === TABS_ID.STANDINGS && (
            <MobileStandingsView
              standings={standings}
              filters={filters}
              onFilterChange={handleFilterChange}
              leagues={leagues}
              seasons={seasons}
              loading={loading}
              onRefresh={refreshData}
              t={t}
            />
          )}

          {activeTab === TABS_ID.TEAMS && (
            <MobileTeamsView
              teams={teams}
              filters={filters}
              onFilterChange={handleFilterChange}
              leagues={leagues}
              seasons={seasons}
              loading={loading}
              onRefresh={refreshData}
              pagination={teamsPagination}
              onPageChange={handleTeamsPageChange}
              t={t}
            />
          )}
        </div>

        {/* Filter Sheets */}
        <DatePickerSheet
          isOpen={showDateSheet}
          onClose={() => setShowDateSheet(false)}
          selectedDate={ensureISODate(railFilters.date || filters.date)}
          onDateSelect={(isoDate) => {
            const formatted = ensureISODate(isoDate, filters.date);
            updateRailFilter("date", formatted);
            setFilters((prev) => ({ ...prev, date: formatted }));
            setShowDateSheet(false);
          }}
        />

        <CountrySheet
          isOpen={showCountrySheet}
          onClose={() => setShowCountrySheet(false)}
          selectedCountries={railFilters.country}
          onCountrySelect={(country) => {
            updateRailFilter('country', country);
            setShowCountrySheet(false);
          }}
          countries={[]}
          multiSelect={false}
        />

        <LeagueSheet
          isOpen={showLeagueSheet}
          onClose={() => setShowLeagueSheet(false)}
          selectedLeagues={railFilters.league}
          onLeagueSelect={(league) => {
            updateRailFilter('league', league);
            setShowLeagueSheet(false);
          }}
          leagues={leagues}
          multiSelect={false}
          sport="volleyball"
        />
      </div>
    </ErrorBoundary>
  );
};

export default VolleyballMobileDashboard;
